import { useEffect, useRef } from "react";

/**
 * 分页搁浅纠偏 hook。
 *
 * 场景:列表页的 page state 独立于数据。当用户**删除当前页(尤其末页)最后一条**、或筛选
 * 使结果变少,数据实际范围缩小到当前 page 之下(page > maxPage),分页请求会落到一个空页并
 * 卡住(显示空态、控件被 `total <= pageSize` 门控隐藏、无法退回)。本 hook 在数据就绪后把 page
 * clamp 回 maxPage。
 *
 * **索引基无关**:调用方在自己的坐标系里传 maxPage —— 0-based 页传 `Math.max(0, ceil(total/size)-1)`;
 * 1-based 页(如知识库)传 `totalPages`(`Math.max(1, ceil(...))`)。
 *
 * **enabled 守卫(易错点)**:只在数据就绪(如 `!isLoading`)时才 clamp。否则首屏 total 未定、
 * maxPage 暂为最小值,会把 URL 深链 / 浏览器回退恢复的 `?page=N` 立刻抹掉。
 *
 * setPage 经 ref 取最新值 —— 兼容 URL-param 更新器这类**每渲染重建的不稳定闭包**,effect 只依赖
 * 真实触发条件(enabled/page/maxPage),不因 setPage 引用变化而每帧重跑。
 */
export function useClampedPage({
  page,
  maxPage,
  setPage,
  enabled = true,
}: {
  page: number;
  maxPage: number;
  setPage: (n: number) => void;
  enabled?: boolean;
}): void {
  const setPageRef = useRef(setPage);
  setPageRef.current = setPage;

  useEffect(() => {
    if (enabled && maxPage >= 0 && page > maxPage) {
      setPageRef.current(maxPage);
    }
  }, [enabled, page, maxPage]);
}
